'use client'

import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { ArrowRight, ChevronDown } from 'lucide-react'
import Ticker from '@/components/ui/Ticker'

const HeroScene = dynamic(() => import('@/components/three/HeroScene'), {
  ssr: false,
  loading: () => <div style={{ width: '100%', height: '100%', background: '#0F172A' }} />,
})

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false)
  const [letterboxClosed, setLetterboxClosed] = useState(false)

  useEffect(() => {
    // Trigger entry animation
    const t1 = setTimeout(() => setLoaded(true), 100)
    // Remove letterbox bars after entry
    const t2 = setTimeout(() => setLetterboxClosed(true), 1600)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.4 } },
  }

  const lineVariants = {
    hidden: { y: '110%', opacity: 0 },
    visible: { y: '0%', opacity: 1, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
  }

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden bg-[#0F172A]">
      {/* Three.js Background */}
      <div className="absolute inset-0 z-0">
        {loaded && <HeroScene />}
      </div>

      {/* Gradient overlays for depth */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0F172A]/60 via-transparent to-[#0F172A]" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#0F172A]/50 via-transparent to-[#0F172A]/30" />

      {/* Letterbox bars */}
      <div
        className="absolute left-0 right-0 top-0 h-[60px] bg-[#020617] z-[50] transition-transform duration-[1200ms]"
        style={{
          transitionTimingFunction: 'cubic-bezier(0.76, 0, 0.24, 1)',
          transform: letterboxClosed ? 'translateY(-100%)' : 'translateY(0)',
        }}
      />
      <div
        className="absolute left-0 right-0 bottom-0 h-[60px] bg-[#020617] z-[50] transition-transform duration-[1200ms]"
        style={{
          transitionTimingFunction: 'cubic-bezier(0.76, 0, 0.24, 1)',
          transform: letterboxClosed ? 'translateY(100%)' : 'translateY(0)',
        }}
      />

      {/* Content */}
      <div className="relative z-[10] h-full flex flex-col justify-center pt-32 pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto w-full">
      
          {/* SEO Optimized Semantic H1 */}
          <h1 className="sr-only">
            Apex Visualtech — Premium Digital Agency in India | Next.js Web Development, Cinematic Videography & Graphic Design
          </h1>

          {/* Main heading (Visual only) */}
          <div aria-hidden="true">
            <div className="overflow-hidden">
              <span
                className="text-[clamp(4rem,14vw,18rem)] leading-[0.85] font-display text-[#F8FAFC] block"
                style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)' }}
              >
                APEX
              </span>
            </div>
            <div className="overflow-hidden pl-[2vw]">
              <div
                className="text-[clamp(4rem,14vw,18rem)] leading-[0.85] font-display block"
                style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)' }}
              >
                <span className="text-[#C8A96E]">VISUAL</span>
                <span className="text-[#F8FAFC]">TECH</span>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <p
            className="mt-6 text-[#94A3B8] text-sm md:text-base tracking-[0.2em]"
            style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
          >
            WHERE TECHNOLOGY MEETS VISION
          </p>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              href="/work"
              className="group inline-flex items-center gap-2 border border-[#C8A96E] text-[#C8A96E] px-7 py-3.5 text-sm md:text-base tracking-[0.12em] hover:bg-[#C8A96E] hover:text-[#0F172A] transition-all duration-300"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              EXPLORE OUR WORK
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              href="/contact"
              className="group inline-flex items-center gap-2 bg-[#C8A96E] text-[#0F172A] px-7 py-3.5 text-sm md:text-base tracking-[0.12em] font-semibold hover:bg-[#D4BA8A] transition-all duration-300"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              START A PROJECT
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-28 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span
            className="text-[#94A3B8] text-[10px] tracking-[0.3em]"
            style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
          >
            SCROLL
          </span>
          <ChevronDown size={16} className="text-[#C8A96E] animate-bounce" />
        </motion.div>
      </div>

      {/* Bottom ticker band */}
      <div className="absolute bottom-0 left-0 right-0 z-[10]">
        <Ticker />
      </div>
    </section>
  )
}
